import type { Icon } from '@tabler/icons-react'
import { Box, Group, Stack, Text, Tooltip, useComputedColorScheme } from '@mantine/core'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShellLayout } from './shellLayout'

type WebNavbarItemProps = {
  label: string
  path: string
  /** When `prefix`, active state matches `location.pathname.startsWith(path)` (e.g. plugin sub-routes). */
  pathMatch?: 'exact' | 'prefix'
  /** If set, used for `navigate()` on click instead of `path` (e.g. prefix `path` + concrete default child). */
  navigateTo?: string
  icon: Icon
  hint: string
  onClick: () => void
  iconSize?: number
  iconStroke?: number
}

export const WebNavbarItem: React.FC<WebNavbarItemProps> = (props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { iconOnly } = useShellLayout()
  const match = props.pathMatch ?? 'exact'
  const isActive = Boolean(
    props.path &&
      (match === 'prefix' ? location.pathname.startsWith(props.path) : location.pathname === props.path),
  )
  const isDark = useComputedColorScheme() === 'dark'
  const hoverBg = isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-1)'
  const unselectedIconColor = isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-6)'
  const activeIconColor = isDark ? 'var(--mantine-color-dark-0)' : 'var(--mantine-color-gray-8)'

  const handleClick = () => {
    props.onClick()
    const target = props.navigateTo ?? props.path
    if (target) navigate(target)
  }

  const pad = iconOnly ? 4 : 8

  const content = (
    <Box
      p={pad}
      style={{
        cursor: 'pointer',
        borderRadius: 8,
        backgroundColor: isActive ? hoverBg : 'transparent',
        border: '1px solid transparent',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: iconOnly ? 'center' : undefined,
      }}
      onClick={handleClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = hoverBg
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isActive ? hoverBg : 'transparent'
      }}
      title={props.hint}
    >
      <Group gap={8}>
        <Box
          style={{
            color: isActive ? activeIconColor : unselectedIconColor,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <props.icon size={props.iconSize ?? 18} stroke={props.iconStroke ?? 1.5} />
        </Box>
        {!iconOnly && (
          <Stack gap={0} style={{ flex: 1 }}>
            <Text size="sm" fw={500} style={isActive ? { color: activeIconColor } : undefined}>
              {props.label}
            </Text>
          </Stack>
        )}
      </Group>
    </Box>
  )

  if (iconOnly) {
    return (
      <Tooltip label={props.label} position="right">
        <span style={{ display: 'inline-block', width: '100%' }}>{content}</span>
      </Tooltip>
    )
  }
  return content
}
